import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from "@angular/router";
import { ProductionBatchState } from "../../../store/batch/batch.state";
import { Color, Lab, PoState, poStateTranslation, ProductionBatch } from "../../../model";
import { NzTableComponent, NzTableQueryParams } from "ng-zorro-antd/table";
import { animate, style, transition, trigger } from "@angular/animations";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { distinctUntilChanged, mergeMap, switchMap, tap } from "rxjs";
import { map } from "rxjs/operators";
import data from 'src/assets/baseMap.json';
import { toUpper } from "lodash";
import { AuthenticationState } from "../../../store/auth/auth.state";
import { UserRole } from "src/model";
import { ActivatedRoute } from "@angular/router";


enum PoStatus {
  RUNNING,
  INSPECTING,
  SUSPENDED,

  APPROVED

}

interface PO {
  id: string;
  label: string;
  status: PoStatus;
  recentlyStartedTime: Date;
  colorCode: string;
  color: Color;

  baseCode: string;

  orderedQuantity: number;
  remainingQuantity: number;
  inspectedQuantity: number;
  failedQuantity: number;
}

@UntilDestroy()
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [
    trigger('highlight', [
      transition(':increment', [
        style({ backgroundColor: 'lightgreen' }),
        animate('3000ms', style({ backgroundColor: 'undefined' })),
      ])
    ])
  ],
  standalone: false
})
export class HomeComponent implements OnInit {
  UserRole = UserRole;
  capital = /(?=[A-Z])/;

  @ViewChild(NzTableComponent, { static: true }) table!: NzTableComponent<ProductionBatch>;
  data: any = data

  poNamePrefix?: string = undefined

  currentOnly: boolean = true;

  filters = {
    inspectionSequence: '',
    state: '',
    scannerId: '',
    poName: '',
    colorCode: '',
    baseMaterial: '',
    producingQuantity: '',
    orderedQuantity: '',
    remainingQuantity: '',
    totalRemainingQuantity: '',
    inspectedQuantity: '',
    totalInspectedQuantity: '',
    failedQuantity: '',
    dEAvg: '',
    dEMax: '',
    ci: '',
    modifiedTime: '',
    startedTime: ''
  };

  sortMap: { [key: string]: 'ascend' | 'descend' | null } = {};

  onSortChange(column: string, order: 'ascend' | 'descend' | null | string): void {
    this.sortMap = {};
    if (order) {
      this.sortMap[column] = order as 'ascend' | 'descend';
    }
  }

  private queryTimeout: any;

  dispatchQuery(): void {
    if (this.queryTimeout) {
      clearTimeout(this.queryTimeout);
    }
    this.queryTimeout = setTimeout(() => {
      const activeFilters: Record<string, string> = {};
      for (const [key, value] of Object.entries(this.filters)) {
        if (value.trim() !== '') {
          activeFilters[key] = value.trim();
        }
      }

      const sortKey = Object.keys(this.sortMap).find(key => this.sortMap[key]);
      const sortDirection = sortKey ? (this.sortMap[sortKey] === 'ascend' ? 1 : -1) : undefined;

      this.state.queryPage({
        pageIndex: 1, // Reset to first page on filter/sort change
        pageSize: this.pageSize,
        filters: activeFilters,
        sortKey: sortKey,
        sortDirection: sortDirection
      });
    }, 400); // 400ms debounce
  }

  // To maintain template compatibility during Work tab, return the unmodified state natively except when on Sort & Filter
  get filteredAndSortedBatches(): ProductionBatch[] {
    return this.state.allLoaded;
  }

  constructor(
    public state: ProductionBatchState,
    public authState: AuthenticationState,
    private route: ActivatedRoute,
    private router: Router) {

  }

  PoState = PoState;
  poStateTranslation = poStateTranslation;
  now = Date.now();

  abortedDisabled: boolean = true;

  virtualItemSize: number = 55;

  pageSize = 50;


  toColorTooltip(color: Color, mult: number): string {
    let res = mult * 3 + "kg - ";
    for (let i = 0; i < 26; i++) {
      let chr = String.fromCharCode(97 + i);
      if ((color as any)[chr]) {
        res += toUpper(chr) + ": " + ((color as any)[chr]) * mult + ", ";
      }
    }
    res = res.slice(0, -2);
    return res;
  }

  colorPicker(color: Lab): string {
    if (!color?.l) return "url(./assets/images/close.png)  center/contain";
    return `lab(${color?.l} ${color?.a} ${color?.b})`;
  }

  get poLabels(): string[] {
    return this.state.allLoaded.map(b => b.poName)
  }

  statusToIcon(status: PoStatus): string {
    switch (status) {
      case PoStatus.RUNNING:
        return "loading"
      case PoStatus.INSPECTING:
        return "search";
      case PoStatus.APPROVED:
        return "check-circle";
      case PoStatus.SUSPENDED:
        return "pause-circle";


      default:
        return "question-circle"
    }
  }

  statusToText(status: PoStatus): string {
    return PoStatus[status];
  }

  selectedTabIndex: number = 0;

  ngOnInit() {
    if (this.router.url.includes('/home/filters')) {
      this.selectedTabIndex = 1;
      this.state.getPage(undefined, undefined, this.poNamePrefix, false);
    } else if (this.router.url.includes('/home/debug') && this.authState.hasRole(UserRole.Admin)) {
      this.selectedTabIndex = 2;
    }

    this.route.queryParams
      .subscribe(params => {
        let prefix = params["prefix"];
        if (prefix) {
          this.onSearch(prefix)
        }
      })
  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;
    if (index === 1) {
      if (this.currentOnly) {
        this.currentOnly = false;
        this.onSliceChange(this.currentOnly);
      } else if (!this.state.allLoaded.length) {
        this.state.getPage(undefined, undefined, this.poNamePrefix, false);
      }
      this.router.navigate(['/home/filters'], { replaceUrl: true, queryParamsHandling: 'preserve' });
    } else if (index === 2 && this.authState.hasRole(UserRole.Admin)) {
      this.router.navigate(['/home/debug/logging'], { replaceUrl: true, queryParamsHandling: 'preserve' });
    } else {
      this.router.navigate(['/home'], { replaceUrl: true, queryParamsHandling: 'preserve' });
    }
  }

  ngAfterViewInit(): void {
    this.table?.cdkVirtualScrollViewport?.scrolledIndexChange.pipe(
      untilDestroyed(this),
      //tap (n => console.log("Index", n)),
      map(n => Math.ceil(n / this.pageSize) + 1),
      distinctUntilChanged(),
      tap(n => console.log("Loading page", n)),
      tap(p => this.state.loadPage(p, this.pageSize, this.poNamePrefix, this.currentOnly))
    )
      .subscribe()
  }

  trackByIndex(_: number, data: ProductionBatch): string {
    return data.id;
  }

  onQueryParamsChange(params: NzTableQueryParams) {
    this.state.getPage(params.pageIndex, params.pageSize)
  }

  truncate(batch: ProductionBatch, number: number) {
    this.state.truncate(batch.id, batch.producingQuantity + number);
  }

  protected readonly toString = toString;
  protected readonly Math = Math;

  cancel() {

  }

  onSearchInput(event: Event) {
    this.onSearch((event.target as HTMLInputElement).value);
  }

  onSearch(prefix: string | undefined) {
    this.poNamePrefix = prefix;
    this.state.getPage(undefined, undefined, this.poNamePrefix)

  }

  onSearchEnter(event: Event) {
    event.preventDefault();
    // Find the first batch matching the current search prefix
    const matchingBatch = this.state.allLoaded.find(b =>
      this.poNamePrefix ? b.poName.toLowerCase().startsWith(this.poNamePrefix.toLowerCase()) : true
    );
    if (matchingBatch) {
      this.router.navigate(['/pos', matchingBatch.id]);
    }
  }

  onSliceChange(currentOnly: boolean) {
    this.state.getPage(undefined, undefined, this.poNamePrefix, currentOnly)
  }
}
